"use client"

import ToggleButtons from "components/Buttons/ToggleButtons"
import Kicker from "components/Kicker"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { CustomEase } from "gsap/all"
import { MorphSVGPlugin } from "gsap/all"
import employee1 from "images/home/advantage/employee1.png"
import employee2 from "images/home/advantage/employee2.png"
import employee3 from "images/home/advantage/employee3.png"
import employee4 from "images/home/advantage/employee4.png"
import employee5 from "images/home/advantage/employee5.png"
import employer1 from "images/home/advantage/employers1.png"
import employer2 from "images/home/advantage/employers2.png"
import employer3 from "images/home/advantage/employers3.png"
import employer4 from "images/home/advantage/employers4.png"
import employer5 from "images/home/advantage/employers5.png"
import { css, fresponsive, ftablet, styled } from "library/styled"
import { useAnimation } from "library/useAnimation"
import { useRef, useState } from "react"
import colors from "styles/colors"
import textStyles from "styles/text"
import links from "utils/links"
import AdvantageContent from "./AdvantageContent"

gsap.registerPlugin(ScrollTrigger, CustomEase, MorphSVGPlugin)

const WILL_CHANGE_TRANSFORM = css`
	will-change: transform;
`

const ALL_EMPLOYEE = [
	{
		heading: "Zero-Cost Everyday Care",
		text: "Arlo plans include $0 virtual urgent-care visits and $0 in-person PCP appointments. Members can text a care team 24/7 or visit their preferred PCP—no bills, no surprise fees, no reason to delay care.",
		image: employee4.src,
		number: "01",
	},
	{
		heading: "Smarter Access,\nLess Stress",
		text: "Employees get a dedicated health guide through an easy to use mobile app—ready to help them navigate benefits, find high-quality providers, and schedule appointments.",
		image: employee1.src,
		number: "02",
	},
	{
		heading: "Personalized Concierge Support",
		text: "Concierge support cuts through the red tape—fewer prior authorizations, claim denials, and less time spent on hold. Employees can focus on getting care, not navigating systems.",
		image: employee2.src,
		number: "03",
	},
	{
		heading: "Care that\n Reaches Out",
		text: "The concierge proactively checks in when better care options are available—ensuring employees always get the highest value care with less effort.",
		image: employee3.src,
		number: "04",
	},
	{
		heading: "Affordable & Flexible",
		text: "Employees can choose care that fits their budget and their quality expectations—no compromises needed.",
		image: employee5.src,
		number: "05",
		about: {
			text: "Learn More About Us",
			link: links.about,
		},
	},
]

const ALL_EMPLOYER = [
	{
		heading: "Concierge Support, Zero HR Headaches",
		text: "Arlo’s 24/7 concierge app fields benefit questions, books cost-efficient care, and delivers $0 virtual urgent-care visits—keeping employees healthy and your HR inbox quiet.",
		image: employer3.src,
		number: "01",
	},
	{
		heading: "Predictable Pricing, Year After Year",
		text: "Avoid budget shocks with consistent, predictable renewal rates—thanks to Arlo’s investment in preventative care and better outcomes.",
		image: employer2.src,
		number: "02",
	},
	{
		heading: "Simple to Administer, Easy to Love",
		text: "Say goodbye to billing chaos and disconnected vendors. Arlo makes setup and ongoing management seamless.",
		image: employer1.src,
		number: "03",
	},
	{
		heading: "Fewer Hassles, More Health",
		text: "No more bouncing between authorizations, denied claims, or confusing paperwork. Employees focus on care—not red tape.",
		image: employer4.src,
		number: "04",
	},
	{
		heading: "Benefits Employees Will Actually Use",
		text: "Arlo offers comprehensive coverage and benefits that teams value—balancing affordability with real access to quality care.",
		image: employer5.src,
		number: "05",
		about: {
			text: "Learn More About Us",
			link: links.about,
		},
	},
]

export default function AdvantageDesktopTablet() {
	const wrapperRef = useRef<HTMLDivElement | null>(null)
	const pinnedWrapper = useRef<HTMLDivElement | null>(null)
	const [selectedOption, setSelectedOption] = useState(0)
	const topSection = useRef<HTMLDivElement | null>(null)

	useAnimation(
		() => {
			if (!wrapperRef.current || !pinnedWrapper.current || !topSection.current)
				return

			const numSections = ALL_EMPLOYEE.length

			const tl = gsap.timeline({
				force3D: true,
				scrollTrigger: {
					trigger: wrapperRef.current,
					start: "top bottom",
					end: "bottom bottom",
					scrub: 0.5,
				},
			})

			gsap.set(topSection.current, { yPercent: 20 })
			for (let i = 0; i < numSections; i++) {
				const progress = i / (numSections - 1)
				const position = (i / numSections) * 100

				tl.to(
					topSection.current,
					{
						yPercent: -position,
						ease: "back.inOut(0.3)",
						duration: 1 / numSections,
					},
					progress,
				).to(
					wrapperRef.current,
					{
						borderRadius: "20px 20px 0 0",
						ease: "linear",
						duration: 0.5 / numSections,
					},
					progress,
				)
			}
		},
		[],
		{ scope: pinnedWrapper },
	)

	return (
		<Wrapper ref={wrapperRef} data-header-hide>
			<PinnedWrapper ref={pinnedWrapper}>
				<Top>
					<StyledKicker doubleIcon size="large">
						Key Benefits
					</StyledKicker>
					<Title>The Arlo Advantage</Title>
				</Top>
				<LineContainer />

				<Bottom>
					<ToggleButtons
						options={["Employees", "Employers"]}
						onChange={setSelectedOption}
					/>
				</Bottom>
				<TopSection>
					<Inner ref={topSection}>
						<Employees visible={selectedOption === 0}>
							{ALL_EMPLOYEE.map((item) => {
								return <AdvantageContent key={item.number} info={item} />
							})}
						</Employees>
						<Employers visible={selectedOption === 1}>
							{ALL_EMPLOYER.map((item) => {
								return (
									<AdvantageContent reversed key={item.number} info={item} />
								)
							})}
						</Employers>
					</Inner>
				</TopSection>
			</PinnedWrapper>
		</Wrapper>
	)
}

const Wrapper = styled("div", {
	...fresponsive(css`
		width: 100%;
		height: 600vh;
		padding: 0;
		will-change: transform;
		background: ${colors.greenMain};
	`),
})

const PinnedWrapper = styled("div", {
	...fresponsive(css`
		position: sticky;
		width: 1440px;
		height: 100lvh;
		padding: 0 32px;
		margin: 0 auto;
		top: 0;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		align-items: center;
		perspective: 1000px;
		${WILL_CHANGE_TRANSFORM}
		overflow: clip;
	`),

	...ftablet(css`
		width: 100%;
		display: flex;
		flex-direction: column;
		grid-template: none;
	`),
})

const StyledKicker = styled(Kicker, {
	...fresponsive(css`
		color: ${colors.greenAccent};
	`),
})

const Title = styled("h1", {
	...fresponsive(css`
		${textStyles.h2};
		color: ${colors.white};
		text-align: center;
		display: block;
	`),
})

const Top = styled("div", {
	...fresponsive(css`
		position: relative;
		width: 100%;
		background: linear-gradient(0deg, #0e5e4900 0%, ${colors.greenMain} 66%);
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: start;
		gap: 24px;
		padding: 8lvh 0;
		z-index: 3;
	`),
})

const Bottom = styled("div", {
	...fresponsive(css`
		position: relative;
		width: 100%;
		height: 20lvh;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 24px;
		z-index: 3;
		background: linear-gradient(180deg, #0e5e4900 0%, ${colors.greenMain} 60%);
	`),
})

const LineContainer = styled("div", {
	...fresponsive(css`
		position: absolute;
		width: 1px;
		height: 100%;
		z-index: -1;
		left: 50%;
		top: 50%;
		background: #0d4d3980;
		transform: translate(-50%, -50%);

		svg {
			width: 100%;
			height: 100%;
			position: absolute;
			top: 0;
			left: 0;
			z-index: -1;
		}
	`),

	...ftablet(css`
		left: 284.2px;
	`),
})

const TopSection = styled("div", {
	...fresponsive(css`
		width: 100%;
		height: 100vh;
		top: 0;
		left: 0;
		position: absolute;
		z-index: 1;
	`),
})

const Employers = styled("div", ({ visible }: { visible: boolean }) => ({
	...fresponsive(css`
		opacity: ${visible ? 1 : 0};
		pointer-events: ${visible ? "auto" : "none"};
		transition: opacity 0.3s ease-in-out;
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
	`),
}))

const Employees = styled("div", ({ visible }: { visible: boolean }) => ({
	...fresponsive(css`
		opacity: ${visible ? 1 : 0};
		transition: opacity 0.3s ease-in-out;
		position: relative;
		pointer-events: ${visible ? "auto" : "none"};
		width: 100%;
		height: 100%;
	`),
}))

const Inner = styled("div", {
	...fresponsive(css`
		position: relative;
		width: 100%;
		height: auto;
	`),
})
